import { FC, useState } from 'react';
import {
  Dropdown,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useAtomValue } from 'jotai';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { FormattedCount } from 'components/formatted-count';
import TooltipDecimal from 'components/tooltip-decimal/tooltip-decimal';
import { formatNumber } from 'utils/number';

import {
  currentDraftSummaryAtom,
  draftSelectedAssetCountAtom,
  draftSelectedSlotCountAtom,
} from '../atoms';
import { MAX_OVERFLOW_NAMES } from '../source-tag-set';
import type {
  NamedAssetListEntity,
  SlotBreakdownDimension,
  SlotSummary,
  SourceRow,
  SourceType,
} from '../types';
import styles from './step1-summary-panel.module.scss';

/** Props for {@link Step1SummaryPanel}. */
export interface Step1SummaryPanelProps {
  /** Which source table the selection came from; `null` when nothing is picked. */
  sourceType: SourceType | null;
  /** Current Step 0 selection — one link per row, in table order. */
  sources: SourceRow[];
  /** The asset list source, when it is the one picked. */
  assetList?: {
    /** Matched names. */
    matched: number;
    /** The uploaded file, if it contributed a matched name. */
    fileName: string | null;
    /** The matched Tracts' owning entities, once each. */
    entities: NamedAssetListEntity[];
  };
}

interface SlotDimensionOption {
  id: SlotBreakdownDimension;
  label: string;
  /** Key on `SlotSummary` — keeps the dropdown decoupled from BE shape names. */
  field: keyof SlotSummary;
}

const SLOT_DIMENSIONS: ReadonlyArray<SlotDimensionOption> = [
  { id: 'wellStatus', label: 'Well Status', field: 'byWellStatus' },
  { id: 'operator', label: 'Operator', field: 'byOperator' },
  { id: 'holeDirection', label: 'Hole Direction', field: 'byHoleDirection' },
  { id: 'formation', label: 'Formation', field: 'byFormation' },
];

/**
 * Sort `[key, count]` entries by count descending, breaking ties alphabetically.
 * @param {Record<string, number>} obj - Key-count map to sort
 * @return {Array<[string, number]>} Sorted entries
 */
const sortEntriesByCountDesc = (
  obj: Record<string, number>
): Array<[string, number]> =>
  Object.entries(obj).sort(([aKey, aCount], [bKey, bCount]) => {
    if (bCount !== aCount) return bCount - aCount;
    return aKey.localeCompare(bKey);
  });

/**
 * Label for the source section. Singular when exactly one source is picked
 * (or none — the empty state reads as a field label), counted when several
 * are. Matches the wizard title's "(N)" treatment. The asset list is always
 * counted, by its matched names.
 *
 * @param {SourceType | null} sourceType - Which table the selection came from
 * @param {number} count - How many sources are selected
 * @return {string} Section heading
 */
const sourceSectionLabel = (
  sourceType: SourceType | null,
  count: number
): string => {
  if (sourceType === 'assetList') {
    return `Asset List (${formatNumber({ value: count, minPrecision: 0 })})`;
  }
  if (sourceType === 'package') {
    return count > 1
      ? `Source Packages (${formatNumber({ value: count, minPrecision: 0 })})`
      : 'Source Package';
  }
  return count > 1
    ? `Entities (${formatNumber({ value: count, minPrecision: 0 })})`
    : 'Entity';
};

/**
 * Step 1 right-rail summary. Lays out a `Package Summary` title, the Step 0
 * selection (one Entity / Source Package link per picked row), and two
 * collapsible sections: Assets (type-counts table + net-acres by
 * instrument-type table) and Slots (dimension `Dropdown` + breakdown table).
 *
 * Reads `currentDraftSummaryAtom` directly — the atom is written by every
 * Step 1 endpoint that touches the draft, so the panel stays in sync with
 * the BE without prop drilling. The source list comes from the form store
 * via the parent, so it tracks Step 0 edits live.
 *
 * @param {Step1SummaryPanelProps} props - Step 0 selection
 * @return {JSX.Element} The summary panel
 */
export const Step1SummaryPanel: FC<Step1SummaryPanelProps> = ({
  sourceType,
  sources,
  assetList,
}) => {
  const [dimension, setDimension] =
    useState<SlotBreakdownDimension>('wellStatus');
  const [assetsOpen, setAssetsOpen] = useState(true);
  const [slotsOpen, setSlotsOpen] = useState(true);

  const { asset: assetSummary = null, slot: slotSummary = null } =
    useAtomValue(currentDraftSummaryAtom) ?? {};

  const assetTotal = useAtomValue(draftSelectedAssetCountAtom);
  const slotTotal = useAtomValue(draftSelectedSlotCountAtom);

  // The asset list names its file, then links the entities its Tracts bring:
  // the first `MAX_OVERFLOW_NAMES`, then how many more, as the source tags do.
  const list = sourceType === 'assetList' ? assetList : undefined;
  const sourceLabel = sourceSectionLabel(
    sourceType,
    list ? list.matched : sources.length
  );
  const fileName = list ? list.fileName : null;
  const links = list ? list.entities.slice(0, MAX_OVERFLOW_NAMES) : sources;
  const moreLinks = list ? list.entities.length - links.length : 0;
  const selectedDimension =
    SLOT_DIMENSIONS.find((d) => d.id === dimension) ?? SLOT_DIMENSIONS[0];
  const slotBreakdown = slotSummary?.[selectedDimension.field] ?? {};

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>Package Summary</h3>

      <section className={styles.sourceSection}>
        <h5 className={styles.sourceLabel}>{sourceLabel}</h5>
        <div className={styles.sourceLinks}>
          {fileName !== null && (
            <span className={styles.sourceName}>{fileName}</span>
          )}
          {links.map((source) => (
            <Link
              key={source.id}
              href={source.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {source.name}
            </Link>
          ))}
          {moreLinks > 0 && (
            <span className={styles.sourceName}>
              and {formatNumber({ value: moreLinks, minPrecision: 0 })} more
            </span>
          )}
          {fileName === null && links.length === 0 && (
            <span className={styles.sourceName}>—</span>
          )}
        </div>
      </section>

      <Accordion expanded={assetsOpen} onClick={setAssetsOpen}>
        <AccordionSummary>
          Assets (<FormattedCount value={assetTotal} />)
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionBody}>
            {assetTotal === 0 ? (
              <p className={styles.empty}>No assets selected</p>
            ) : (
              <>
                <Table size="sm" className={styles.summaryTable}>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Asset Type</TableHeader>
                      <TableHeader>Count</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortEntriesByCountDesc(
                      assetSummary?.byAssetType ?? {}
                    ).map(([type, count]) => (
                      <TableRow key={type}>
                        <TableCell>{type}</TableCell>
                        <TableCell>
                          <FormattedCount value={count} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {assetSummary &&
                  Object.keys(assetSummary.netAcresByInstrumentType).length >
                    0 && (
                    <Table size="sm" className={styles.summaryTable}>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Instrument Type</TableHeader>
                          <TableHeader>Net Acres</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortEntriesByCountDesc(
                          assetSummary.netAcresByInstrumentType
                        ).map(([instrument, acres]) => (
                          <TableRow key={instrument}>
                            <TableCell>{instrument}</TableCell>
                            <TableCell>
                              <TooltipDecimal value={acres} /> ac
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
              </>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={slotsOpen} onClick={setSlotsOpen}>
        <AccordionSummary>
          Slots (<FormattedCount value={slotTotal} />)
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionBody}>
            {slotTotal === 0 ? (
              <p className={styles.empty}>No slots selected</p>
            ) : (
              <>
                <Dropdown
                  id="step1-slots-dimension"
                  titleText="View Summary By"
                  label="Select dimension"
                  light
                  items={[...SLOT_DIMENSIONS]}
                  itemToString={(item: SlotDimensionOption | null) =>
                    item?.label ?? ''
                  }
                  selectedItem={selectedDimension}
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: SlotDimensionOption | null;
                  }) => {
                    if (selectedItem !== null) {
                      setDimension(selectedItem.id);
                    }
                  }}
                  size="sm"
                  className={styles.dropdown}
                />
                <Table size="sm" className={styles.summaryTable}>
                  <TableHead>
                    <TableRow>
                      <TableHeader>{selectedDimension.label}</TableHeader>
                      <TableHeader>Count</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortEntriesByCountDesc(slotBreakdown).map(
                      ([key, count]) => (
                        <TableRow key={key}>
                          <TableCell>{key || '—'}</TableCell>
                          <TableCell>
                            <FormattedCount value={count} />
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
